import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Space, Spin, Typography } from 'antd';
import { publicApi } from '../../services/apiInstances';

const { Text } = Typography;

/**
 * WP-10 slice 4 — pay, or put a deposit down, from the approved estimate.
 *
 *   "Customers also must have the approved estimate screen able to make a
 *    payment, deposit, manage this ticket in that way"
 *
 * ── This component never decides an amount ────────────────────────────────
 *
 * It renders figures the server computed and sends back a CHOICE — 'full' or
 * 'deposit' — and nothing else. The amount that reaches Stripe is derived
 * server-side from the estimate's own total and the shop's deposit percentage.
 * `/checkout-session` took its charge amount from the request body until #109,
 * and the ledger ended up recording a figure Stripe never charged; there is no
 * longer any path by which a number from this file becomes money.
 *
 * ── "Paid" is a fact from the server, never from the URL ──────────────────
 *
 * State comes from `GET /payment-state`, which reads settled payments. The
 * return from Stripe is a *hint* that a webhook may be in flight — it is used
 * to poll and to word the message, never to claim a payment happened. The
 * portal invoice page used to show "Payment received" on `?paid=1` alone, which
 * congratulated anyone who opened Stripe's page and pressed Back.
 */

export interface EstimatePaymentState {
  total_cents: number;
  paid_cents: number;
  balance_cents: number;
  is_paid_in_full: boolean;
  deposit_percent: number;
  deposit_target_cents: number;
  payable: boolean;
}

export interface EstimatePaymentPanelProps {
  token: string;
  /** True when this page load came back from Stripe's hosted page. */
  returningFromCheckout?: boolean;
  /** Stripe's own id for the session just paid, echoed back in the return URL. */
  checkoutSessionId?: string | null;
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export const EstimatePaymentPanel: React.FC<EstimatePaymentPanelProps> = ({
  token,
  returningFromCheckout = false,
  checkoutSessionId = null,
}) => {
  const [state, setState] = useState<EstimatePaymentState | null>(null);
  const [starting, setStarting] = useState<'full' | 'deposit' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [awaitingWebhook, setAwaitingWebhook] = useState(returningFromCheckout);

  const load = useCallback(async () => {
    try {
      const res = await publicApi.get(`/public/estimates/${token}/payment-state`);
      setState(res.data);
      return res.data as EstimatePaymentState;
    } catch (e) {
      // Non-fatal: the estimate itself must still render. A customer who cannot
      // see their estimate because the payment panel failed is worse off than
      // one who sees it without the panel.
      console.error('Error loading payment state:', e);
      return null;
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  /**
   * Coming back from Stripe.
   *
   * Polling alone was not enough: payment-state only changes when the WEBHOOK
   * lands, and with none delivered a paid customer sat here watching an unpaid
   * estimate (seen live — Stripe held $374.50 as paid while the shop's books
   * showed it owing). So the return now hands the server the session id and
   * asks it to check with Stripe, THEN polls for the result it wrote.
   *
   * The id is not treated as proof of anything: the server looks the session up
   * at Stripe and records only what Stripe calls paid. Polling stays as the
   * fallback for the case where the webhook wins the race.
   */
  useEffect(() => {
    if (!returningFromCheckout) return;
    let cancelled = false;
    let attempts = 0;

    const reconcile = async () => {
      if (!checkoutSessionId) return;
      try {
        await publicApi.post(
          `/public/estimates/${token}/checkout/${checkoutSessionId}/reconcile`,
          {},
        );
      } catch {
        // Non-fatal: the poll below still picks up a webhook that lands.
      }
    };

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      const next = await load();
      if (cancelled) return;
      if (next?.is_paid_in_full || attempts >= 10) {
        setAwaitingWebhook(false);
        return;
      }
      setTimeout(tick, 2000);
    };

    const t = setTimeout(async () => {
      await reconcile();
      tick();
    }, 800);
    return () => { cancelled = true; clearTimeout(t); };
  }, [returningFromCheckout, checkoutSessionId, token, load]);

  const start = async (kind: 'full' | 'deposit') => {
    setStarting(kind);
    setError(null);
    try {
      // The body carries the CHOICE only. No amount.
      const res = await publicApi.post(`/public/estimates/${token}/checkout-session`, { kind });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }
      setError('Could not start the payment. Please try again.');
    } catch (e: any) {
      // WP-03 keeps `error` a plain string, so it is safe to render directly.
      setError(e?.response?.data?.error || 'Could not start the payment. Please try again.');
      await load();
    } finally {
      setStarting(null);
    }
  };

  if (!state) return null;
  if (!state.payable) return null;

  if (state.is_paid_in_full) {
    return (
      <Card style={{ marginBottom: 16 }} data-testid="estimate-payment-panel">
        <Alert type="success" showIcon message="Paid in full — thank you!" />
      </Card>
    );
  }

  const depositDueCents = Math.max(0, state.deposit_target_cents - state.paid_cents);
  const canDeposit = depositDueCents > 0 && depositDueCents < state.balance_cents;

  return (
    <Card style={{ marginBottom: 16 }} data-testid="estimate-payment-panel">
      {awaitingWebhook && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Confirming your payment…"
          description="This usually takes a few seconds."
        />
      )}
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={error}
          data-testid="estimate-payment-error"
        />
      )}

      <div style={{ marginBottom: 12 }}>
        <Text strong style={{ fontSize: 16 }}>Payment</Text>
        {state.paid_cents > 0 && (
          <div style={{ fontSize: 13, color: '#6b7280' }} data-testid="estimate-paid-so-far">
            {money(state.paid_cents)} paid so far
          </div>
        )}
        <div style={{ fontSize: 13, color: '#6b7280' }} data-testid="estimate-balance">
          Balance due {money(state.balance_cents)}
        </div>
      </div>

      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Button
          type="primary"
          size="large"
          block
          loading={starting === 'full'}
          disabled={starting !== null}
          onClick={() => start('full')}
          data-testid="pay-full"
        >
          Pay in full — {money(state.balance_cents)}
        </Button>
        {canDeposit && (
          <Button
            size="large"
            block
            loading={starting === 'deposit'}
            disabled={starting !== null}
            onClick={() => start('deposit')}
            data-testid="pay-deposit"
          >
            Pay {state.deposit_percent}% deposit — {money(depositDueCents)}
          </Button>
        )}
      </Space>

      {starting !== null && (
        <div style={{ textAlign: 'center', marginTop: 8 }}>
          <Spin size="small" /> <Text type="secondary">Taking you to secure checkout…</Text>
        </div>
      )}
      <div style={{ marginTop: 10 }}>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Payments are handled by Stripe. Card details are entered on Stripe's
          secure page and are never sent to or stored by the shop.
        </Text>
      </div>
    </Card>
  );
};

export default EstimatePaymentPanel;
